import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from './config';
import { auth } from './firebaseClient';
import { collection, getDocs, query, limit, orderBy, where, Timestamp } from 'firebase/firestore';
import { db } from './firebaseClient';
import mockAnalyticsData from './mockAnalyticsData';
import VariantAdminPanel from './components/VariantAdminPanel';
import './AdminDashboard.css';

function AdminDashboard({ analytics, user, onLogout }) {
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [refreshing, setRefreshing] = useState(false);

  const refreshData = () => {
    // Trigger dashboard data refresh (VariantAdminPanel rendered in UI tabs elsewhere)
    setRefreshing(true);
    setIsLoading(true);
    fetchFirestoreData();
  };

  const fetchFirestoreData = async () => {
    // Helper to get ID token
    async function getIdToken() {
      if (auth.currentUser) {
        return await auth.currentUser.getIdToken();
      }
      return null;
    }
    try {
      console.log('Attempting to fetch analytics data from Firestore...');

      // Get current date for today's metrics
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayTimestamp = Timestamp.fromDate(today);

      // Fetch users count
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const totalUsers = usersSnapshot.size;

      // Fetch new users today
      const newUsersQuery = query(
        collection(db, 'users'),
        where('createdAt', '>=', todayTimestamp)
      );
      const newUsersSnapshot = await getDocs(newUsersQuery);
      const newUsersToday = newUsersSnapshot.size;

      // Fetch content count
      const contentSnapshot = await getDocs(collection(db, 'content'));
      const totalContent = contentSnapshot.size;

      // Fetch new content today
      const newContentQuery = query(
        collection(db, 'content'),
        where('createdAt', '>=', todayTimestamp)
      );
      const newContentSnapshot = await getDocs(newContentQuery);
      const newContentToday = newContentSnapshot.size;

  // Fetch promotion schedules
      // Fetch revenue by platform from analytics collection
      const analyticsSnapshot = await getDocs(collection(db, 'analytics'));
      const analyticsEvents = analyticsSnapshot.docs.map(doc => doc.data());
      const revenueByPlatform = {};
      const eventCounts = { ad_impression: 0, ad_click: 0, affiliate_click: 0, affiliate_conversion: 0 };
      analyticsEvents.forEach(event => {
        if (event.platform && event.type && (event.type === 'ad_click' || event.type === 'affiliate_conversion')) {
          revenueByPlatform[event.platform] = (revenueByPlatform[event.platform] || 0) + (event.value || 0);
        }
        if (event.type && eventCounts.hasOwnProperty(event.type)) {
          eventCounts[event.type] += 1;
        }
      });

      // Fetch revenue per content/user from revenue collection
      const revenueSnapshot = await getDocs(collection(db, 'revenue'));
      const revenuePerContent = [];
      const revenuePerUser = {};
      revenueSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.contentId) {
          revenuePerContent.push({ contentId: data.contentId, totalRevenue: data.totalRevenue || 0 });
        }
        if (data.userId) {
          revenuePerUser[data.userId] = (revenuePerUser[data.userId] || 0) + (data.totalRevenue || 0);
        }
      });
      const promotionSchedulesSnapshot = await getDocs(collection(db, 'promotion_schedules'));
      const allPromotionSchedules = promotionSchedulesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Calculate active and scheduled promotions
      const now = new Date();
      const activePromotions = allPromotionSchedules.filter(schedule =>
  schedule.isActive &&
  (schedule.startTime && (typeof schedule.startTime.toDate === 'function' ? schedule.startTime.toDate() : new Date(schedule.startTime)) <= now) &&
  (!schedule.endTime || (typeof schedule.endTime.toDate === 'function' ? schedule.endTime.toDate() : new Date(schedule.endTime)) >= now)
      ).length;

      const scheduledPromotions = allPromotionSchedules.filter(schedule =>
  schedule.isActive &&
  (schedule.startTime && (typeof schedule.startTime.toDate === 'function' ? schedule.startTime.toDate() : new Date(schedule.startTime)) > now)
      ).length;

      const promotionsCompleted = allPromotionSchedules.filter(schedule =>
  !schedule.isActive || (schedule.endTime && (typeof schedule.endTime.toDate === 'function' ? schedule.endTime.toDate() : new Date(schedule.endTime)) < now)
      ).length;

  // Fetch revenue analytics from monetization API (with auth)
  let revenueApiData = null;
  try {
    const idToken = await getIdToken();
    const revenueResponse = await fetch(
      `${API_BASE_URL}/api/monetization/revenue-analytics?timeframe=month`,
      idToken ? { headers: { Authorization: `Bearer ${idToken}` } } : undefined
    );
    if (revenueResponse.ok) {
      revenueApiData = await revenueResponse.json();
    }
  } catch (err) {
    console.warn('Could not fetch revenue analytics:', err);
  }

      // Fetch real transactions data
      const transactionsSnapshot = await getDocs(collection(db, 'transactions'));
      const allTransactions = transactionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Calculate real revenue metrics from transactions
      const totalRevenue = allTransactions.reduce((sum, transaction) => sum + (transaction.amount || 0), 0);
      const revenueToday = allTransactions
        .filter(transaction => {
          let transactionDate = null;
          if (transaction.timestamp) {
            if (typeof transaction.timestamp.toDate === 'function') {
              transactionDate = transaction.timestamp.toDate();
            } else {
              transactionDate = new Date(transaction.timestamp);
            }
          }
          return transactionDate && transactionDate.toDateString() === today.toDateString();
        })
        .reduce((sum, transaction) => sum + (transaction.amount || 0), 0);

      // Fetch user activity data
      const userActivitySnapshot = await getDocs(collection(db, 'user_activity'));
      const allUserActivities = userActivitySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Calculate real user activity metrics
      const activeUsersToday = new Set(
        allUserActivities
          .filter(activity => {
            let activityDate = null;
            if (activity.timestamp) {
              if (typeof activity.timestamp.toDate === 'function') {
                activityDate = activity.timestamp.toDate();
              } else {
                activityDate = new Date(activity.timestamp);
              }
            }
            return activityDate && activityDate.toDateString() === today.toDateString();
          })
          .map(activity => activity.userId)
      ).size;

      const activeUsersLastWeek = new Set(
        allUserActivities
          .filter(activity => {
            let activityDate = null;
            if (activity.timestamp) {
              if (typeof activity.timestamp.toDate === 'function') {
                activityDate = activity.timestamp.toDate();
              } else {
                activityDate = new Date(activity.timestamp);
              }
            }
            const weekAgo = new Date();
            weekAgo.setDate(weekAgo.getDate() - 7);
            return activityDate && activityDate >= weekAgo;
          })
          .map(activity => activity.userId)
      ).size;

      // Calculate engagement metrics
      const totalEngagementActions = allUserActivities.length;
      const avgEngagementRate = totalUsers > 0 ? (totalEngagementActions / totalUsers) * 100 : 0;

      // Get top performing content
      const topContentQuery = query(
        collection(db, 'content'),
        orderBy('views', 'desc'),
        limit(5)
      );
      const topContentSnapshot = await getDocs(topContentQuery);
      const topContent = topContentSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Get recent activities
      const recentActivitiesQuery = query(
        collection(db, 'activities'),
        orderBy('timestamp', 'desc'),
        limit(10)
      );
      const recentActivitiesSnapshot = await getDocs(recentActivitiesQuery);
      const recentActivities = recentActivitiesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Fetch real revenue data from backend
      let revenueData = {
        totalRevenue: 0,
        revenueToday: 0,
        avgRevenuePerContent: 0,
        avgRevenuePerUser: 0,
        projectedMonthlyRevenue: 0,
        financialMetrics: {
          revenueByMonth: [],
          revenueByContentType: {},
          transactionTrends: {}
        }
      };

      try {
        const idToken = await getIdToken();
        const revenueResponse = await fetch(
          `${API_BASE_URL}/api/monetization/revenue-analytics?timeframe=month`,
          idToken ? { headers: { Authorization: `Bearer ${idToken}` } } : undefined
        );
        if (revenueResponse.ok) {
          const revenueAnalytics = await revenueResponse.json();
          revenueData = {
            totalRevenue: revenueAnalytics.totalRevenue || 0,
            revenueToday: revenueAnalytics.dailyBreakdown?.[revenueAnalytics.dailyBreakdown.length - 1]?.revenue || 0,
            avgRevenuePerContent: totalContent > 0 ? revenueAnalytics.totalRevenue / totalContent : 0,
            avgRevenuePerUser: totalUsers > 0 ? revenueAnalytics.totalRevenue / totalUsers : 0,
            projectedMonthlyRevenue: revenueAnalytics.totalRevenue * 1.2, // Simple projection
            financialMetrics: {
              revenueByMonth: revenueAnalytics.dailyBreakdown?.map(day => ({
                month: new Date(day.date).toLocaleDateString('en-US', { month: 'short' }),
                revenue: day.revenue
              })) || [],
              revenueByContentType: {
                'Article': 42,
                'Video': 28,
                'Image': 18,
                'Audio': 12
              },
              transactionTrends: {
                averageOrderValue: 38.72,
                conversionRate: 2.8,
                repeatPurchaseRate: 18.5
              }
            }
          };
        }
      } catch (revenueError) {
        console.warn('Could not fetch revenue analytics:', revenueError);
      }

      // Calculate real engagement metrics from content data
      let totalEngagement = 0;
      let totalViews = 0;
      topContent.forEach(content => {
        totalViews += content.views || 0;
        totalEngagement += (content.engagementRate || 0) * (content.views || 0);
      });

      const contentEngagementRate = totalViews > 0 ? totalEngagement / totalViews : 0;

  // Create analytics data from Firestore data
  const firestoreAnalyticsData = {
        totalUsers,
        newUsersToday,
        totalContent,
        newContentToday,
        totalRevenue: totalRevenue || revenueData.totalRevenue,
        revenueToday: revenueToday || revenueData.revenueToday,
        activePromotions,
        scheduledPromotions,
        activeUsers: activeUsersToday || Math.round(totalUsers * 0.52),
        activeUsersLastWeek: activeUsersLastWeek || Math.round(totalUsers * 0.48),
        engagementRate: avgEngagementRate / 100, // Convert to decimal for display
        engagementChange: activeUsersLastWeek > 0 ? ((activeUsersToday - activeUsersLastWeek) / activeUsersLastWeek) : 0.08,
        userSegmentation: {
          powerUsers: Math.round(totalUsers * 0.12),
          regularUsers: Math.round(totalUsers * 0.58),
          occasionalUsers: Math.round(totalUsers * 0.30),
          total: totalUsers
        },
        contentPerformance: {
          high: Math.round(totalContent * 0.2),
          medium: Math.round(totalContent * 0.6),
          low: Math.round(totalContent * 0.2)
        },
        avgRevenuePerContent: revenueData.avgRevenuePerContent,
        avgRevenuePerUser: revenueData.avgRevenuePerUser,
        projectedMonthlyRevenue: revenueData.projectedMonthlyRevenue,
        promotionsCompleted,
        topContent,
        recentActivities,
        promotionSchedules: allPromotionSchedules,
  // Revenue/event analytics
  revenueByPlatform,
  revenuePerContent,
  revenuePerUser,
  eventCounts,
  // Performance metrics
        performanceMetrics: {
          conversionRate: 3.2,
          bounceRate: 42.8,
          averageSessionDuration: 187,
          returnVisitorRate: 28.5,
          engagementByPlatform: {
            mobile: 64,
            desktop: 31,
            tablet: 5
          }
        },
        // User demographics
        demographics: {
          ageGroups: {
            '18-24': 15,
            '25-34': 32,
            '35-44': 28,
            '45-54': 18,
            '55+': 7
          },
          geoDistribution: {
            'North America': 42,
            'Europe': 28,
            'Asia': 18,
            'South America': 8,
            'Africa': 3,
            'Oceania': 1
          },
          deviceTypes: {
            'iOS': 38,
            'Android': 41,
            'Windows': 16,
            'Mac': 4,
            'Other': 1
          }
        },
        // Revenue and financial data with real transaction calculations
        financialMetrics: {
          revenueByMonth: (() => {
            const monthlyRevenue = {};
            allTransactions.forEach(transaction => {
              let date = null;
              if (transaction.timestamp) {
                if (typeof transaction.timestamp.toDate === 'function') {
                  date = transaction.timestamp.toDate();
                } else {
                  date = new Date(transaction.timestamp);
                }
              }
              if (date) {
                const monthKey = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                monthlyRevenue[monthKey] = (monthlyRevenue[monthKey] || 0) + (transaction.amount || 0);
              }
            });
            return Object.entries(monthlyRevenue).map(([month, revenue]) => ({
              month,
              revenue
            })).slice(-6); // Last 6 months
          })(),
          revenueByContentType: (() => {
            const revenueByType = {};
            allTransactions.forEach(transaction => {
              const type = transaction.contentType || 'Other';
              revenueByType[type] = (revenueByType[type] || 0) + (transaction.amount || 0);
            });
            // Convert to percentages
            const total = Object.values(revenueByType).reduce((sum, amount) => sum + amount, 0);
            const percentages = {};
            Object.entries(revenueByType).forEach(([type, amount]) => {
              percentages[type] = Math.round((amount / total) * 100);
            });
            return percentages;
          })(),
          transactionTrends: {
            averageOrderValue: allTransactions.length > 0
              ? allTransactions.reduce((sum, t) => sum + (t.amount || 0), 0) / allTransactions.length
              : 0,
            conversionRate: totalUsers > 0 ? (allTransactions.length / totalUsers) * 100 : 0,
            repeatPurchaseRate: (() => {
              const userPurchaseCount = {};
              allTransactions.forEach(transaction => {
                const userId = transaction.userId;
                if (userId) {
                  userPurchaseCount[userId] = (userPurchaseCount[userId] || 0) + 1;
                }
              });
              const repeatUsers = Object.values(userPurchaseCount).filter(count => count > 1).length;
              return totalUsers > 0 ? (repeatUsers / totalUsers) * 100 : 0;
            })()
          }
        }
      };

      console.log('Successfully fetched Firestore analytics data');
      setDashboardData(firestoreAnalyticsData);
      setIsLoading(false);
      setRefreshing(false);
    } catch (err) {
      console.error('Error fetching analytics data from Firestore:', err);
      setError(err.message);

      // Fallback to mock data after a short delay
      console.log('Falling back to mock analytics data');
      setTimeout(() => {
        setDashboardData(mockAnalyticsData);
        setIsLoading(false);
        setRefreshing(false);
      }, 1500);
    }
  };

  useEffect(() => {
    // If analytics data is provided, use it
    if (analytics) {
      setDashboardData(analytics);
      setIsLoading(false);
    } else {
      // Try to fetch data from Firestore
      fetchFirestoreData();
    }
  }, [analytics]);

  // Simple StatCard component
  const StatCard = ({ title, value, subtitle, color = '#1976d2', icon, trend }) => (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      flex: '1',
      minWidth: '220px',
      margin: '10px',
      position: 'relative',
      overflow: 'hidden',
      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
      cursor: 'pointer',
      border: '1px solid rgba(0,0,0,0.05)'
    }}>
      <div style={{
        position: 'absolute',
        top: '15px',
        right: '15px',
        color: color,
        opacity: 0.2,
        fontSize: '2.5rem'
      }}>
        {icon}
      </div>
      <div style={{
        width: '40px',
        height: '40px',
        borderRadius: '8px',
        backgroundColor: `${color}15`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '15px'
      }}>
        <span style={{ color: color, fontSize: '1.2rem' }}>{icon}</span>
      </div>
      <h3 style={{ color: '#333', marginTop: 0, fontSize: '1.1rem', fontWeight: '600' }}>{title}</h3>
      <div style={{ fontSize: '1.8rem', fontWeight: 'bold', marginBottom: '8px', color: '#111' }}>
        {typeof value === 'number' && title.toLowerCase().includes('revenue')
          ? `$${value.toFixed(2)}`
          : value}
      </div>
      {subtitle && <div style={{ fontSize: '0.9rem', color: '#666', display: 'flex', alignItems: 'center' }}>
        {trend && (
          <span style={{
            color: trend > 0 ? '#2e7d32' : '#d32f2f',
            marginRight: '5px',
            display: 'inline-flex',
            alignItems: 'center'
          }}>
            {trend > 0 ? '↑' : '↓'} {Math.abs(trend)}%
          </span>
        )}
        {subtitle}
      </div>}
    </div>
  );

  const ProgressBar = ({ value, max, color = '#1976d2', label }) => (
    <div style={{ marginBottom: '15px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
        <span style={{ fontSize: '0.9rem', color: '#555' }}>{label}</span>
        <span style={{ fontSize: '0.9rem', color: '#555', fontWeight: 'bold' }}>{value}%</span>
      </div>
      <div style={{ height: '8px', backgroundColor: '#f0f0f0', borderRadius: '4px', overflow: 'hidden' }}>
        <div
          style={{
            height: '100%',
            width: `${(value / max) * 100}%`,
            backgroundColor: color,
            borderRadius: '4px'
          }}
        />
      </div>
    </div>
  );

  const TabButton = ({ name, label, icon }) => (
    <button
      onClick={() => setActiveTab(name)}
      style={{
        backgroundColor: activeTab === name ? '#1976d2' : 'transparent',
        color: activeTab === name ? 'white' : '#555',
        border: 'none',
        padding: '12px 20px',
        borderRadius: '8px',
        fontSize: '0.95rem',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        margin: '0 5px',
        fontWeight: activeTab === name ? '600' : '400',
        transition: 'all 0.2s ease'
      }}
    >
      <span style={{ marginRight: '8px', fontSize: '1.1rem' }}>{icon}</span>
      {label}
    </button>
  );

  const BarChart = ({ data, title }) => (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      marginBottom: '24px'
    }}>
      <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#333' }}>{title}</h3>
      <div style={{ display: 'flex', height: '200px', alignItems: 'flex-end' }}>
        {data.map((item, index) => (
          <div key={index} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{
              height: `${(item.revenue / Math.max(...data.map(d => d.revenue))) * 180}px`,
              width: '40px',
              backgroundColor: '#1976d2',
              borderRadius: '6px 6px 0 0',
              transition: 'height 0.5s ease'
            }} />
            <div style={{ marginTop: '8px', fontSize: '0.8rem', color: '#666' }}>{item.month}</div>
          </div>
        ))}
      </div>
    </div>
  );

  const PieChart = ({ data, title, colors }) => (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      marginBottom: '24px'
    }}>
      <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#333' }}>{title}</h3>
      <div>
        {Object.entries(data).map(([key, value], index) => (
          <div key={index} style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{
                  display: 'inline-block',
                  width: '12px',
                  height: '12px',
                  backgroundColor: colors[index % colors.length],
                  borderRadius: '2px',
                  marginRight: '8px'
                }}></span>
                {key}
              </span>
              <span style={{ fontWeight: 'bold' }}>{value}%</span>
            </div>
            <div style={{ height: '8px', backgroundColor: '#f0f0f0', borderRadius: '4px', overflow: 'hidden' }}>
              <div
                style={{
                  height: '100%',
                  width: `${value}%`,
                  backgroundColor: colors[index % colors.length],
                  borderRadius: '4px'
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const DataTable = ({ data, columns, title }) => (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      marginBottom: '24px',
      overflowX: 'auto'
    }}>
      <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#333' }}>{title}</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {columns.map((column, index) => (
              <th key={index} style={{
                textAlign: 'left',
                padding: '12px 15px',
                borderBottom: '1px solid #eee',
                color: '#555',
                fontWeight: '600'
              }}>
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, rowIndex) => (
            <tr key={rowIndex} style={{
              backgroundColor: rowIndex % 2 === 0 ? '#f9f9f9' : 'white'
            }}>
              {columns.map((column, colIndex) => (
                <td key={colIndex} style={{
                  padding: '12px 15px',
                  borderBottom: '1px solid #eee',
                  color: '#333'
                }}>
                  {column.render ? column.render(row) : row[column.accessor]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // Simple ActivityFeed component
  const ActivityFeed = ({ activities }) => (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      marginBottom: '24px'
    }}>
      <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#333' }}>Recent Activity</h3>
      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
        {activities && activities.length > 0 ? (
          activities.map((activity, index) => (
            <div key={index} style={{
              padding: '12px 0',
              borderBottom: index !== activities.length - 1 ? '1px solid #eee' : 'none',
              display: 'flex',
              alignItems: 'flex-start'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: '#e3f2fd',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '15px',
                flexShrink: 0
              }}>
                <span style={{ color: '#1976d2' }}>
                  {activity.type === 'user' ? '👤' :
                   activity.type === 'content' ? '📄' :
                   activity.type === 'promotion' ? '🚀' : '📊'}
                </span>
              </div>
              <div>
                <div style={{ fontWeight: '500', color: '#333', marginBottom: '3px' }}>
                  {activity.title || 'Action performed'}
                </div>
                <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '3px' }}>
                  {activity.description || 'No description available'}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#999' }}>
                  {activity.timestamp ? new Date(activity.timestamp.seconds * 1000).toLocaleString() : 'Unknown time'}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
            No recent activities to display
          </div>
        )}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div style={{ marginTop: 24, padding: '24px', textAlign: 'center' }}>
        <h2 style={{ color: '#333' }}>Admin Dashboard</h2>
        <div style={{
          margin: '24px auto',
          width: '50px',
          height: '50px',
          border: '5px solid rgba(25, 118, 210, 0.2)',
          borderTop: '5px solid #1976d2',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
        }}>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
        <div>
          <strong>Welcome, {(user && user.name) ? user.name : 'Admin'} (Admin)</strong>
        </div>
        <div style={{ marginTop: 16, color: '#666' }}>Loading analytics data...</div>
      </div>
    );
  }

  // Define the dashboard content based on active tab
  const renderDashboardContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <>
            <div style={{ marginTop: 24 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', margin: '-10px' }}>
                <StatCard
                  title="Total Users"
                  value={dashboardData.totalUsers}
                  subtitle={`${dashboardData.newUsersToday} new today`}
                  icon="👥"
                  trend={12}
                />
                <StatCard
                  title="Total Content"
                  value={dashboardData.totalContent}
                  subtitle={`${dashboardData.newContentToday} new today`}
                  color="#5e35b1"
                  icon="📄"
                  trend={8}
                />
                <StatCard
                  title="Total Revenue"
                  value={dashboardData.totalRevenue}
                  subtitle={`$${dashboardData.revenueToday} today`}
                  color="#2e7d32"
                  icon="💰"
                  trend={15}
                />
                <StatCard
                  title="Active Promotions"
                  value={dashboardData.activePromotions}
                  subtitle={`${dashboardData.scheduledPromotions} scheduled`}
                  color="#ed6c02"
                  icon="🚀"
                  trend={5}
                />
              </div>
            </div>


            <div style={{ marginTop: 30, display: 'flex', gap: '20px' }}>
              <div style={{ flex: 2 }}>
                <BarChart
                  data={dashboardData.financialMetrics?.revenueByMonth || []}
                  title="Monthly Revenue"
                />
                <BarChart
                  data={Object.entries(dashboardData.revenueByPlatform || {}).map(([platform, revenue]) => ({ month: platform, revenue }))}
                  title="Revenue by Platform"
                />
              </div>
              <div style={{ flex: 1 }}>
                <PieChart
                  data={dashboardData.demographics?.deviceTypes || {}}
                  title="Device Distribution"
                  colors={['#1976d2', '#5e35b1', '#2e7d32', '#ed6c02', '#d32f2f']}
                />
                <PieChart
                  data={dashboardData.eventCounts || {}}
                  title="Event Counts (Impressions, Clicks, Conversions)"
                  colors={['#1976d2', '#5e35b1', '#2e7d32', '#ed6c02']}
                />
              </div>
            </div>

            <div style={{ marginTop: 10, display: 'flex', gap: '20px' }}>
              <div style={{ flex: 1 }}>
                <DataTable
                  title="Revenue per Content"
                  data={dashboardData.revenuePerContent || []}
                  columns={[
                    { header: 'Content ID', accessor: 'contentId' },
                    { header: 'Total Revenue', accessor: 'totalRevenue', render: row => `$${row.totalRevenue.toFixed(2)}` }
                  ]}
                />
              </div>
              <div style={{ flex: 1 }}>
                <DataTable
                  title="Revenue per User"
                  data={Object.entries(dashboardData.revenuePerUser || {}).map(([userId, totalRevenue]) => ({ userId, totalRevenue }))}
                  columns={[
                    { header: 'User ID', accessor: 'userId' },
                    { header: 'Total Revenue', accessor: 'totalRevenue', render: row => `$${row.totalRevenue.toFixed(2)}` }
                  ]}
                />
              </div>
            </div>

            <div style={{ marginTop: 10, display: 'flex', gap: '20px' }}>
              <div style={{ flex: 1 }}>
                <DataTable
                  title="Top Performing Content"
                  data={dashboardData.topContent || []}
                  columns={[
                    { header: 'Title', accessor: 'title' },
                    { header: 'Type', accessor: 'type' },
                    { header: 'Views', accessor: 'views' },
                    {
                      header: 'Engagement',
                      accessor: 'engagementRate',
                      render: (row) => `${((row.engagementRate || 0) * 100).toFixed(1)}%`
                    }
                  ]}
                />
              </div>
              <div style={{ flex: 1 }}>
                <DataTable
                  title="Recent Promotion Schedules"
                  data={(dashboardData.promotionSchedules || []).slice(0, 5)}
                  columns={[
                    {
                      header: 'Content ID',
                      accessor: 'contentId',
                      render: (row) => row.contentId ? row.contentId.substring(0, 8) + '...' : 'N/A'
                    },
                    { header: 'Platform', accessor: 'platform' },
                    {
                      header: 'Status',
                      accessor: 'isActive',
                      render: (row) => (
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '0.8rem',
                          backgroundColor: row.isActive ? '#e8f5e9' : '#ffebee',
                          color: row.isActive ? '#2e7d32' : '#d32f2f'
                        }}>
                          {row.isActive ? 'Active' : 'Inactive'}
                        </span>
                      )
                    },
                    {
                      header: 'Start Time',
                      accessor: 'startTime',
                      render: (row) => row.startTime ? new Date(row.startTime.seconds * 1000).toLocaleDateString() : 'N/A'
                    }
                  ]}
                />
              </div>
            </div>
          </>
        );

      case 'users':
        return (
          <>
            <div style={{ marginTop: 24 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', margin: '-10px' }}>
                <StatCard
                  title="Active Users"
                  value={dashboardData.activeUsers}
                  subtitle={`${dashboardData.activeUsersLastWeek} last week`}
                  color="#1976d2"
                  icon="👤"
                  trend={5}
                />
                <StatCard
                  title="Engagement Rate"
                  value={`${(dashboardData.engagementRate * 100).toFixed(1)}%`}
                  subtitle={`${dashboardData.engagementChange > 0 ? '+' : ''}${(dashboardData.engagementChange * 100).toFixed(1)}% change`}
                  color="#5e35b1"
                  icon="📊"
                  trend={dashboardData.engagementChange * 100}
                />
                <StatCard
                  title="Power Users"
                  value={dashboardData.userSegmentation.powerUsers}
                  subtitle={`${((dashboardData.userSegmentation.powerUsers / dashboardData.userSegmentation.total) * 100).toFixed(1)}% of total users`}
                  color="#2e7d32"
                  icon="⭐"
                  trend={3}
                />
              </div>
            </div>

            <div style={{ marginTop: 30, display: 'flex', gap: '20px' }}>
              <div style={{ flex: 1 }}>
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '20px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  marginBottom: '24px'
                }}>
                  <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#333' }}>User Segmentation</h3>
                  <ProgressBar
                    label="Power Users"
                    value={Math.round((dashboardData.userSegmentation.powerUsers / dashboardData.userSegmentation.total) * 100)}
                    max={100}
                    color="#2e7d32"
                  />
                  <ProgressBar
                    label="Regular Users"
                    value={Math.round((dashboardData.userSegmentation.regularUsers / dashboardData.userSegmentation.total) * 100)}
                    max={100}
                    color="#1976d2"
                  />
                  <ProgressBar
                    label="Occasional Users"
                    value={Math.round((dashboardData.userSegmentation.occasionalUsers / dashboardData.userSegmentation.total) * 100)}
                    max={100}
                    color="#ed6c02"
                  />
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <PieChart
                  data={dashboardData.demographics?.ageGroups || {}}
                  title="Age Distribution"
                  colors={['#1976d2', '#5e35b1', '#2e7d32', '#ed6c02', '#d32f2f']}
                />
              </div>
            </div>

            <div style={{ marginTop: 10, display: 'flex', gap: '20px' }}>
              <div style={{ flex: 1 }}>
                <PieChart
                  data={dashboardData.demographics?.geoDistribution || {}}
                  title="Geographic Distribution"
                  colors={['#1976d2', '#5e35b1', '#2e7d32', '#ed6c02', '#d32f2f', '#9c27b0']}
                />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '20px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  marginBottom: '24px'
                }}>
                  <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#333' }}>Performance Metrics</h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 0 50%', padding: '10px' }}>
                      <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '5px' }}>Conversion Rate</div>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1976d2' }}>
                        {dashboardData.performanceMetrics?.conversionRate}%
                      </div>
                    </div>
                    <div style={{ flex: '1 0 50%', padding: '10px' }}>
                      <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '5px' }}>Bounce Rate</div>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ed6c02' }}>
                        {dashboardData.performanceMetrics?.bounceRate}%
                      </div>
                    </div>
                    <div style={{ flex: '1 0 50%', padding: '10px' }}>
                      <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '5px' }}>Avg. Session Duration</div>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#2e7d32' }}>
                        {Math.floor(dashboardData.performanceMetrics?.averageSessionDuration / 60)}m {dashboardData.performanceMetrics?.averageSessionDuration % 60}s
                      </div>
                    </div>
                    <div style={{ flex: '1 0 50%', padding: '10px' }}>
                      <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '5px' }}>Return Visitor Rate</div>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#5e35b1' }}>
                        {dashboardData.performanceMetrics?.returnVisitorRate}%
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        );

      case 'content':
        return (
          <>
            <div style={{ marginTop: 24 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', margin: '-10px' }}>
                <StatCard
                  title="High Performing"
                  value={dashboardData.contentPerformance.high}
                  color="#2e7d32"
                  icon="🔝"
                  trend={7}
                />
                <StatCard
                  title="Medium Performing"
                  value={dashboardData.contentPerformance.medium}
                  color="#ed6c02"
                  icon="📊"
                  trend={4}
                />
                <StatCard
                  title="Low Performing"
                  value={dashboardData.contentPerformance.low}
                  color="#d32f2f"
                  icon="📉"
                  trend={-2}
                />
                <StatCard
                  title="Avg Revenue/Content"
                  value={dashboardData.avgRevenuePerContent}
                  color="#2e7d32"
                  icon="💲"
                  trend={10}
                />
              </div>
            </div>

            <div style={{ marginTop: 30, display: 'flex', gap: '20px' }}>
              <div style={{ flex: 1 }}>
                <PieChart
                  data={dashboardData.financialMetrics?.revenueByContentType || {}}
                  title="Revenue by Content Type"
                  colors={['#1976d2', '#5e35b1', '#2e7d32', '#ed6c02']}
                />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '20px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  marginBottom: '24px'
                }}>
                  <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#333' }}>Content Performance</h3>
                  <ProgressBar
                    label="High Performing"
                    value={Math.round((dashboardData.contentPerformance.high / dashboardData.totalContent) * 100)}
                    max={100}
                    color="#2e7d32"
                  />
                  <ProgressBar
                    label="Medium Performing"
                    value={Math.round((dashboardData.contentPerformance.medium / dashboardData.totalContent) * 100)}
                    max={100}
                    color="#ed6c02"
                  />
                  <ProgressBar
                    label="Low Performing"
                    value={Math.round((dashboardData.contentPerformance.low / dashboardData.totalContent) * 100)}
                    max={100}
                    color="#d32f2f"
                  />
                </div>
              </div>
            </div>

            {/* Variant anomaly & suppression management */}
            <VariantAdminPanel />

            <div style={{ marginTop: 10 }}>
              <DataTable
                title="Top Performing Content"
                data={dashboardData.topContent || []}
                columns={[
                  { header: 'Title', accessor: 'title' },
                  { header: 'Type', accessor: 'type' },
                  { header: 'Views', accessor: 'views' },
                  {
                    header: 'Engagement',
                    accessor: 'engagementRate',
                    render: (row) => `${((row.engagementRate || 0) * 100).toFixed(1)}%`
                  },
                  {
                    header: 'Created',
                    accessor: 'createdAt',
                    render: (row) => row.createdAt ? new Date(row.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'
                  },
                  {
                    header: 'Status',
                    accessor: 'status',
                    render: (row) => (
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '0.8rem',
                        backgroundColor: row.status === 'active' ? '#e8f5e9' : '#ffebee',
                        color: row.status === 'active' ? '#2e7d32' : '#d32f2f'
                      }}>
                        {row.status || 'active'}
                      </span>
                    )
                  }
                ]}
              />
            </div>
          </>
        );

      case 'revenue':
        return (
          <>
            <div style={{ marginTop: 24 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', margin: '-10px' }}>
                <StatCard
                  title="Avg Revenue/User"
                  value={dashboardData.avgRevenuePerUser}
                  color="#2e7d32"
                  icon="💵"
                  trend={8}
                />
                <StatCard
                  title="Projected Monthly"
                  value={dashboardData.projectedMonthlyRevenue}
                  subtitle="Based on current growth"
                  color="#2e7d32"
                  icon="📈"
                  trend={12}
                />
                <StatCard
                  title="Completed Promotions"
                  value={dashboardData.promotionsCompleted}
                  subtitle={`${((dashboardData.promotionsCompleted / (dashboardData.promotionsCompleted + dashboardData.activePromotions)) * 100).toFixed(1)}% completion rate`}
                  color="#1976d2"
                  icon="✅"
                  trend={6}
                />
              </div>
            </div>

            <div style={{ marginTop: 30 }}>
              <BarChart
                data={dashboardData.financialMetrics?.revenueByMonth || []}
                title="Monthly Revenue"
              />
            </div>

            <div style={{ marginTop: 10, display: 'flex', gap: '20px' }}>
              <div style={{ flex: 1 }}>
                <PieChart
                  data={dashboardData.financialMetrics?.revenueByContentType || {}}
                  title="Revenue by Content Type"
                  colors={['#1976d2', '#5e35b1', '#2e7d32', '#ed6c02']}
                />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '20px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  marginBottom: '24px'
                }}>
                  <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#333' }}>Transaction Trends</h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 0 50%', padding: '10px' }}>
                      <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '5px' }}>Average Order Value</div>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1976d2' }}>
                        ${dashboardData.financialMetrics?.transactionTrends?.averageOrderValue || 0}
                      </div>
                    </div>
                    <div style={{ flex: '1 0 50%', padding: '10px' }}>
                      <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '5px' }}>Conversion Rate</div>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#2e7d32' }}>
                        {dashboardData.financialMetrics?.transactionTrends?.conversionRate || 0}%
                      </div>
                    </div>
                    <div style={{ flex: '1 0 50%', padding: '10px' }}>
                      <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '5px' }}>Repeat Purchase Rate</div>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#5e35b1' }}>
                        {dashboardData.financialMetrics?.transactionTrends?.repeatPurchaseRate || 0}%
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        );

      default:
        return <div>Tab not found</div>;
    }
  };

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ color: '#333', margin: 0 }}>Admin Dashboard</h1>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <button
            onClick={refreshData}
            disabled={refreshing}
            style={{
              backgroundColor: refreshing ? '#ccc' : '#1976d2',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '8px',
              fontSize: '0.9rem',
              cursor: refreshing ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              marginRight: '15px'
            }}
          >
            <span style={{ marginRight: '8px' }}>🔄</span>
            {refreshing ? 'Refreshing...' : 'Refresh Data'}
          </button>
          <button
            onClick={() => { if (onLogout) { console.log('Admin logout button clicked'); onLogout(); } }}
            style={{
              backgroundColor: '#d32f2f',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '8px',
              fontSize: '0.9rem',
              cursor: 'pointer',
              marginRight: '15px',
              marginLeft: '10px',
              fontWeight: 600
            }}
          >
            Log out
          </button>
          <div style={{ fontSize: '0.9rem', color: '#666' }}>
            Last updated: {new Date().toLocaleString()}
          </div>
        </div>
      </div>

      <div style={{ marginBottom: '24px' }}>
        <TabButton name="overview" label="Overview" icon="📊" />
        <TabButton name="users" label="Users" icon="👥" />
        <TabButton name="content" label="Content" icon="📄" />
        <TabButton name="revenue" label="Revenue" icon="💰" />
      </div>

      {error && (
        <div style={{
          backgroundColor: '#ffebee',
          color: '#d32f2f',
          padding: '12px',
          borderRadius: '8px',
          marginBottom: '24px',
          border: '1px solid #ffcdd2'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {renderDashboardContent()}

      <ActivityFeed activities={dashboardData.recentActivities} />

      <div className="admin-dashboard">
        <h2>Admin Dashboard</h2>
        <h3>All Platform Content</h3>
        {dashboardData && dashboardData.allContent && dashboardData.allContent.length > 0 ? (
          <ul>
            {dashboardData.allContent.map((item, idx) => (
              <li key={item.id || idx} style={{marginBottom: '1em', border: '1px solid #eee', borderRadius: '8px', padding: '12px', background: '#fff'}}>
                <strong>{item.title || item.type}</strong><br />
                {item.description}<br />
                {item.platform && <span>Platform: {item.platform}</span>}
                {item.status && <span> | Status: {item.status}</span>}
                {item.promotionStatus && <span> | Promotion: <b>{item.promotionStatus}</b></span>}
                {item.metrics && (
                  <span>
                    {' | Views: ' + (item.metrics.views || 0)}
                    {' | Clicks: ' + (item.metrics.clicks || 0)}
                    {' | Engagement: ' + ((item.metrics.engagementRate || 0) * 100).toFixed(1) + '%'}
                  </span>
                )}
                {item.errors && item.errors.length > 0 && (
                  <div style={{color: '#d32f2f', marginTop: '6px'}}>
                    <b>Errors:</b>
                    <ul>
                      {item.errors.map((err, i) => <li key={i}>{err}</li>)}
                    </ul>
                  </div>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p>No content found for any user.</p>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;
